export enum KIBANA_LOG_LEVEL {
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

interface KibanaLogMeta {
  source: string;
  context?: string;
  url?: string;
  username?: string;
  version?: string;
  /** Frontend route the user was on when the log fired (window.location.pathname). */
  page?: string;
  /** Full URL incl. query string (filters state) — restores the exact app state. */
  page_url?: string;
  /** OpenReplay session replay deep link for the moment the log fired. */
  session_url?: string;
  /** OpenReplay session id — also stamped into the message as
   * `openReplaySession.id=<id>` so OpenReplay's Elastic integration can
   * correlate these logs to replays. */
  openreplay_session_id?: string;
  /** True when the user was shown an error dialog for this failure. */
  user_facing?: boolean;
  /**
   * Error-analytics fields, derived server-side by dash-meta's KibanaLogger
   * (never sent by clients). `level` is mirrored here because the log
   * pipeline strips the top-level `level` key during JSON parsing.
   */
  level?: KIBANA_LOG_LEVEL;
  fingerprint?: string;
  is_test_account?: boolean;
  client?: string;
  retried?: boolean;
  /** Retry attempt number (N from `Attempt N/M`) — present only on retry logs. */
  retry_attempt?: number;
  /** Max retry attempts (M) — lets consumers tell a soft retry from the final one. */
  retry_max_attempts?: number;
  /** True only for a non-final retry (may still recover); absent otherwise. The
   * dashboard hides these by default. */
  soft_retry?: boolean;
  /** True when the error fired during a maintenance window (Unleash
   * `maintenance` flag); absent otherwise. The dashboard hides these by
   * default — expected breakage, not signal. */
  maintenance?: boolean;
  gql_operation?: string;
}

export interface PageLoadLog {
  page: string;
  completed: boolean;
  reason: string; // 'loaded', 'unloaded', 'unmounted'
  timestamp: string; // loading start UTC timestamp
  start: number; // time in ms since timeOrigin
  duration_ms: number; // active duration (total - background)
  total_ms?: number;
  background_ms?: number;
  request_ids?: string[];
  visibility_changes?: Partial<PerformanceEntry>[]; // visible and hidden events since timeOrigin
}

export interface GQLQueryLog {
  query: string;
  active_requests: string[];
  additional_metadata?: Record<string, unknown>;
}

export interface KibanaLog {
  '@timestamp': string;
  level: KIBANA_LOG_LEVEL;
  message: string;
  meta: KibanaLogMeta;
  'request-id'?: string;
  'correlation-id'?: string;
  page_load?: PageLoadLog;
  gql_query?: GQLQueryLog;
}

type KibanaLogFlat = Omit<KibanaLog, 'meta'> & KibanaLogMeta;

export type LogBody = Omit<KibanaLogFlat, '@timestamp'>;

export interface LogResponse {
  success: boolean;
  message: string;
}
